/**
 * StyledTextField Component (Refactored)
 *
 * A theme-aware text field component with built-in field-level validation.
 * Uses Material-UI v7 TextField with dynamic theme integration.
 *
 * IMPORTANT:
 * - Uses ONLY theme tokens (NO hardcoded values)
 * - Handles validation at field level (NOT section level)
 * - Supports all Material-UI TextField features
 * - Optimized with React.memo
 * - Modular architecture with separate concerns
 *
 * @example Basic Usage
 * ```tsx
 * <StyledTextField
 *   value={name}
 *   onChange={setName}
 *   label="Full Name"
 *   placeholder="Enter your name"
 *   required={true}
 * />
 * ```
 *
 * @example Password with Toggle
 * ```tsx
 * <StyledTextField
 *   value={password}
 *   onChange={setPassword}
 *   type={showPassword ? 'text' : 'password'}
 *   label="Password"
 *   startAdornment={<LockIcon />}
 *   endAdornment={
 *     <IconButton onClick={() => setShowPassword(!showPassword)}>
 *       {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
 *     </IconButton>
 *   }
 * />
 * ```
 */

import React, { useRef } from 'react';
import { Box, Fade, useTheme } from '@mui/material';

// Component imports
import { FieldUpdateIndicator } from '../../../dialogs/FieldUpdateIndicator/FieldUpdateIndicator';

// Modular imports
import type { StyledTextFieldProps } from './StyledTextField.types';
import { AnimatedTextField, UpdateIndicatorWrapper } from './StyledTextField.styles';
import { useFieldValidation, useValidationEventHandlers } from './StyledTextField.validation';
import { useFieldTracking } from './StyledTextField.tracking';
import { useInputAdornments } from './StyledTextField.adornments';
import {
  getAccessibilityProps,
  getInputProps,
  getHtmlInputProps,
  getFieldStyles,
  getFieldWidth,
  getTypeAttributes,
  shallowCompareProps
} from './StyledTextField.utils';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const StyledTextField = React.memo<StyledTextFieldProps>(({
  // Core props
  value,
  onChange,

  // Form integration
  name,
  label,
  placeholder,
  helperText,
  required = false,
  disabled = false,
  readOnly = false,

  // Input configuration
  type = 'text',
  multiline = false,
  rows,
  maxRows,
  minRows,
  autoComplete,
  autoFocus = false,
  autoSelect = false,
  spellCheck,
  fullWidth,

  // Input constraints
  maxLength,
  minLength,
  pattern,

  // Validation props
  error: externalError = false,
  errorMessage: externalErrorMessage,
  rules = [],
  validateOnChange = false,
  validateOnBlur = false,
  onValidationChange,
  validateEmail = false,
  validateUrl = false,
  customValidation,

  // Theme integration
  variant = 'outlined',
  size = 'medium',
  color = 'primary',

  // Input adornments
  startAdornment,
  endAdornment,
  InputProps,
  inputProps: nativeInputProps,

  // Accessibility
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,

  // Event handlers
  onFocus,
  onBlur,
  onKeyDown,
  onKeyUp,

  // Custom styling
  sx,

  // Field update tracking
  fieldPath,
  showUpdateIndicator = false,
  trackFieldUpdates = false,
  onFieldUpdate,

  // Other props
  ...otherProps
}) => {
  const theme = useTheme();
  const previousValueRef = useRef<string>(value);
  // Stable ID: generated once per component instance, not on every render
  const stableId = useRef(`styled-text-field-${Math.random().toString(36).substr(2, 9)}`);

  // ============================================================================
  // HOOKS INTEGRATION
  // ============================================================================

  // Validation logic
  const validation = useFieldValidation({
    value,
    rules,
    validateEmail,
    validateUrl,
    customValidation,
    required,
    type,
    name,
    label,
    onValidationChange,
    error: externalError,
    errorMessage: externalErrorMessage,
    validateOnChange,
    validateOnBlur,
  } as StyledTextFieldProps);

  // Field tracking
  const tracking = useFieldTracking({
    fieldPath,
    trackFieldUpdates,
    showUpdateIndicator,
    onFieldUpdate,
    value,
  } as StyledTextFieldProps);

  // Input adornments
  const inputAdornments = useInputAdornments({
    startAdornment,
    endAdornment,
    InputProps,
  } as StyledTextFieldProps);

  // Event handlers with validation
  const eventHandlers = useValidationEventHandlers(
    {
      onChange,
      onBlur,
      onFocus,
      validateOnChange,
      validateOnBlur,
      autoSelect,
      maxLength,
      value,
    } as StyledTextFieldProps,
    validation.validateField
  );

  // ============================================================================
  // COMPUTED PROPS
  // ============================================================================

  // Accessibility props
  const accessibilityProps = getAccessibilityProps({
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    required,
    error: validation.hasError,
    name,
    helperText,
    errorMessage: validation.displayErrorMessage,
  } as StyledTextFieldProps);

  // Input props (the `InputProps`/`slotProps.input` wrapper-level slot)
  const inputProps = getInputProps({
    autoComplete,
    autoFocus,
    spellCheck,
    readOnly,
    name,
    ...getTypeAttributes(type),
  } as StyledTextFieldProps, accessibilityProps);

  // Native-element-only attributes — MUST reach `slotProps.htmlInput`
  // (legacy `inputProps`), never `slotProps.input` (legacy `InputProps`),
  // or `maxLength`/`minLength`/`pattern` silently land on the wrapper `<div>`
  // instead of the actual `<input>`/`<textarea>` (see StyledTextField.utils.ts).
  const htmlInputProps = {
    ...nativeInputProps,
    ...getHtmlInputProps({
      maxLength: maxLength ?? nativeInputProps?.maxLength,
      minLength: minLength ?? nativeInputProps?.minLength,
      pattern: pattern ?? nativeInputProps?.pattern,
    } as StyledTextFieldProps),
  };

  // Field styles
  const fieldStyles = getFieldStyles(theme, {
    hasError: validation.hasError,
    hasRecentUpdate: tracking.hasRecentUpdate,
    updateSource: tracking.updateSource,
    theme,
  });

  // Field width
  const fieldWidth = getFieldWidth(fullWidth, theme);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Box sx={{ width: fieldWidth, position: 'relative', ...sx }}>
      <AnimatedTextField
        // Core props
        value={value}
        onChange={eventHandlers.handleChange}

        // Form integration
        id={name ? `field-${name}` : stableId.current}
        name={name}
        label={label}
        placeholder={placeholder}
        helperText={validation.displayErrorMessage || helperText}
        required={required}
        disabled={disabled}

        // Input configuration
        type={type}
        multiline={multiline}
        rows={rows}
        maxRows={maxRows}
        minRows={minRows}
        fullWidth={true} // Always full width within container

        // Theme integration
        variant={variant}
        size={size}
        color={color}

        // Validation state
        error={validation.hasError}

        // Event handlers
        onFocus={eventHandlers.handleFocus}
        onBlur={eventHandlers.handleBlur}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}

        // Styling and animations
        hasRecentUpdate={tracking.hasRecentUpdate}
        updateSource={tracking.updateSource}
        sx={fieldStyles}

        // Accessibility
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid !== undefined ? ariaInvalid : validation.hasError}

        // Input props with adornments
        slotProps={{
          input: {
            ...inputAdornments,
            ...inputProps,
          },
          htmlInput: htmlInputProps,
        }}

        // Pass through other props
        {...otherProps}
      />

      {/* Field Update Indicator */}
      {showUpdateIndicator && tracking.hasRecentUpdate && fieldPath && (
        <UpdateIndicatorWrapper>
          <Fade in={tracking.hasRecentUpdate} timeout={300}>
            <div>
              <FieldUpdateIndicator
                fieldPath={fieldPath}
                showTooltip={true}
                autoHide={true}
                autoHideDelay={8000}
              />
            </div>
          </Fade>
        </UpdateIndicatorWrapper>
      )}
    </Box>
  );
}, shallowCompareProps);

// ============================================================================
// COMPONENT METADATA
// ============================================================================

// Set display name for debugging
StyledTextField.displayName = 'StyledTextField';

// ============================================================================
// EXPORTS
// ============================================================================

export { StyledTextField as default };

// Re-export types for convenience
export type { StyledTextFieldProps } from './StyledTextField.types';